import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../api/client';
import { FlaskConical, Plus, Upload, ChevronDown, ChevronRight, X } from 'lucide-react';
import DoseResponseChart from './DoseResponseChart';
import RegistrationSection from './RegistrationSection';
import ELNSection from './ELNSection';
import StructureImage from '../StructureImage';
import { useAuth } from '../../../context/AuthContext';

// =====================================================================
// Types
// =====================================================================

interface BatchListItem {
  batch_id: number;
  batch_code: string;
  chemist_initials: string;
  smiles: string;
  inchi_key: string | null;
  status: string;
  purity_percent: number | null;
  amount_mg: number | null;
  synthesis_date: string | null;
  created_at: string | null;
  notes: string | null;
}

interface BatchDetail {
  batch_id: number;
  batch_code: string;
  chemist_initials: string;
  notebook_number: number;
  salt_suffix: string | null;
  smiles: string;
  inchi_key: string | null;
  project_id: number;
  status: string;
  purity_percent: number | null;
  amount_mg: number | null;
  synthesis_date: string | null;
  created_at: string | null;
  registered_lot_id: number | null;
  notes: string | null;
  submissions: {
    submission_id: number;
    assay_name: string;
    submitted_at: string | null;
    status: string;
    completed_at: string | null;
    notes: string | null;
  }[];
  statistics: {
    stat_id: number;
    assay_name: string;
    measurement_type: string;
    n_replicates: number | null;
    mean_value: number | null;
    std_deviation: number | null;
    ci_low: number | null;
    ci_high: number | null;
    curve_fit_r2: number | null;
    hill_slope: number | null;
  }[];
  registration: {
    parent_code: string;
    version_code: string;
    lot_code: string;
  } | null;
}

interface DataRow {
  concentration: string;
  concentration_unit: string;
  response_value: string;
  response_unit: string;
  replicate_number: string;
}

interface BatchesTabProps {
  smiles: string;
  inchiKey?: string | null;
  projectId?: number;
}

// =====================================================================
// Badges
// =====================================================================

const BATCH_STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  active:     { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
  submitted:  { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  registered: { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  retired:    { bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0' },
};

const BatchStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = BATCH_STATUS_COLORS[status] || BATCH_STATUS_COLORS.active;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize"
      style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
      {status}
    </span>
  );
};

const SUB_STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending:     { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
  in_progress: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  completed:   { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  failed:      { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
};

const SubStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors = SUB_STATUS_COLORS[status] || SUB_STATUS_COLORS.pending;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs"
      style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}>
      {status.replace('_', ' ')}
    </span>
  );
};

// =====================================================================
// Helpers
// =====================================================================

function r2Color(r2: number | null): string {
  if (r2 === null) return '#6b7280';
  if (r2 > 0.99) return '#10b981';
  if (r2 > 0.95) return '#f59e0b';
  return '#ef4444';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

const MEASUREMENT_TYPES = ['IC50', 'Ki', 'EC50', '% inhibition', 'Kd'];

function emptyRow(): DataRow {
  return { concentration: '', concentration_unit: 'nM', response_value: '', response_unit: 'percent_activity', replicate_number: '1' };
}

// =====================================================================
// Shared styles
// =====================================================================

const INPUT_STYLE: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 12,
  backgroundColor: '#ffffff', color: '#1e293b',
  border: '1px solid #d1d5db', borderRadius: 4, outline: 'none',
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2,
};

const SMALL_INPUT: React.CSSProperties = {
  padding: '4px 6px', fontSize: 11, fontFamily: 'monospace',
  backgroundColor: '#ffffff', color: '#1e293b',
  border: '1px solid #d1d5db', borderRadius: 3, outline: 'none',
  width: '100%',
};

const TEAL_BTN: React.CSSProperties = {
  padding: '6px 16px', fontSize: 12, borderRadius: 6, border: 'none',
  backgroundColor: '#0d9488', color: '#ffffff', cursor: 'pointer',
};

const CANCEL_BTN: React.CSSProperties = {
  padding: '6px 12px', fontSize: 12, borderRadius: 6, border: 'none',
  backgroundColor: 'transparent', color: '#6b7280', cursor: 'pointer',
};

// =====================================================================
// Component
// =====================================================================

const BatchesTab: React.FC<BatchesTabProps> = ({ smiles, inchiKey, projectId }) => {
  const { user } = useAuth();

  // Derive 4-letter initials from user's full_name, padded with 'X'
  const userInitials = React.useMemo(() => {
    if (!user?.full_name) return '';
    const parts = user.full_name.trim().split(/\s+/);
    const letters = parts.map(p => p.charAt(0).toUpperCase()).join('');
    return (letters + 'XXXX').slice(0, 4);
  }, [user?.full_name]);

  // List state
  const [batches, setBatches] = useState<BatchListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  // Creation form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formInitials, setFormInitials] = useState('');
  const [formPurity, setFormPurity] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().slice(0, 10));
  const [formSalt, setFormSalt] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  // Submit for screen
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitAssay, setSubmitAssay] = useState('');
  const [submitNotes, setSubmitNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  // Data entry
  const [showDataEntry, setShowDataEntry] = useState(false);
  const [entryAssay, setEntryAssay] = useState('');
  const [entryMeasurement, setEntryMeasurement] = useState('IC50');
  const [dataRows, setDataRows] = useState<DataRow[]>([emptyRow(), emptyRow(), emptyRow()]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);

  // CSV upload
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [csvResult, setCsvResult] = useState<any>(null);

  // Raw data
  const [showRawData, setShowRawData] = useState(false);
  const [rawData, setRawData] = useState<any>(null);

  // Chart data (for dose-response charts)
  const [chartData, setChartData] = useState<any>(null);
  const [chartLoading, setChartLoading] = useState(false);

  const resetCreateForm = useCallback(() => {
    setFormInitials(userInitials); setFormPurity(''); setFormAmount('');
    setFormDate(new Date().toISOString().slice(0, 10));
    setFormSalt(''); setFormNotes(''); setCreateError(null); setCreateSuccess(null);
  }, [userInitials]);

  // Auto-populate initials when form opens
  useEffect(() => {
    if (showCreateForm && !formInitials && userInitials) {
      setFormInitials(userInitials);
    }
  }, [showCreateForm, userInitials]);

  // ─── Fetch batch list ───
  useEffect(() => {
    if (!projectId) return;
    const fetchBatches = async () => {
      setLoading(true); setError(null);
      try {
        const resp = await apiClient.get('/batches/list', { params: { project_id: projectId } });
        const all: BatchListItem[] = resp.data;
        setBatches(inchiKey ? all.filter(b => b.inchi_key === inchiKey) : all);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load batches');
      } finally { setLoading(false); }
    };
    fetchBatches();
  }, [projectId, inchiKey, refreshKey]);

  // ─── Fetch detail ───
  useEffect(() => {
    if (!expandedCode) { setDetail(null); return; }
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const resp = await apiClient.get(`/batches/${expandedCode}`);
        setDetail(resp.data);
      } catch { setDetail(null); }
      finally { setDetailLoading(false); }
    };
    fetchDetail();
  }, [expandedCode, detailRefreshKey]);

  // ─── Fetch chart data when batch with stats is expanded ───
  useEffect(() => {
    if (!expandedCode || !detail || detail.statistics.length === 0) {
      setChartData(null);
      return;
    }
    const fetchChartData = async () => {
      setChartLoading(true);
      try {
        const resp = await apiClient.get(`/batches/assays/${expandedCode}`);
        setChartData(resp.data);
      } catch { setChartData(null); }
      finally { setChartLoading(false); }
    };
    fetchChartData();
  }, [expandedCode, detail, detailRefreshKey]);

  const handleCardClick = (batchCode: string) => {
    setExpandedCode(prev => (prev === batchCode ? null : batchCode));
    setShowSubmitForm(false); setShowDataEntry(false); setUploadResult(null);
    setCsvResult(null); setShowRawData(false); setRawData(null);
    setChartData(null);
  };

  // ─── Create batch ───
  const handleCreate = async () => {
    if (!projectId || formInitials.length < 2 || formInitials.length > 5) return;
    setCreating(true); setCreateError(null); setCreateSuccess(null);
    try {
      const body: Record<string, any> = { chemist_initials: formInitials, smiles, project_id: projectId };
      if (formPurity) body.purity_percent = parseFloat(formPurity);
      if (formAmount) body.amount_mg = parseFloat(formAmount);
      if (formDate) body.synthesis_date = formDate;
      if (formSalt) body.salt_suffix = formSalt;
      if (formNotes.trim()) body.notes = formNotes.trim();
      const resp = await apiClient.post('/batches/create', body);
      setCreateSuccess(`Batch ${resp.data.batch_code} created`);
      setTimeout(() => { setShowCreateForm(false); resetCreateForm(); setRefreshKey(k => k + 1); }, 1500);
    } catch (err: any) { setCreateError(err?.response?.data?.detail || 'Failed to create batch'); }
    finally { setCreating(false); }
  };

  // ─── Submit for screen ───
  const handleSubmitForScreen = async () => {
    if (!expandedCode || submitAssay.length < 2) return;
    setSubmitting(true); setSubmitError(null); setSubmitSuccess(null);
    try {
      await apiClient.post(`/batches/${expandedCode}/submit-for-screen`, {
        assay_name: submitAssay, notes: submitNotes.trim() || null,
      });
      setSubmitSuccess(`Submitted for ${submitAssay}`);
      setTimeout(() => {
        setShowSubmitForm(false); setSubmitAssay(''); setSubmitNotes(''); setSubmitSuccess(null);
        setDetailRefreshKey(k => k + 1); setRefreshKey(k => k + 1);
      }, 1500);
    } catch (err: any) { setSubmitError(err?.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  // ─── Upload assay data (manual) ───
  const handleUploadData = async () => {
    if (!expandedCode || !entryAssay) return;
    const validRows = dataRows.filter(r => r.concentration && r.response_value);
    if (validRows.length === 0) return;
    setUploading(true); setUploadError(null); setUploadResult(null);
    try {
      const resp = await apiClient.post('/batches/assays/upload', {
        batch_code: expandedCode,
        assay_name: entryAssay,
        measurement_type: entryMeasurement,
        data: validRows.map(r => ({
          concentration: parseFloat(r.concentration),
          concentration_unit: r.concentration_unit,
          response_value: parseFloat(r.response_value),
          response_unit: r.response_unit,
          replicate_number: parseInt(r.replicate_number) || 1,
        })),
      });
      setUploadResult(resp.data);
      setShowDataEntry(false);
      setDetailRefreshKey(k => k + 1);
    } catch (err: any) { setUploadError(err?.response?.data?.detail || 'Failed to upload data'); }
    finally { setUploading(false); }
  };

  // ─── CSV upload ───
  const handleCsvUpload = async (file: File) => {
    setCsvUploading(true); setCsvError(null); setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await apiClient.post('/batches/assays/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCsvResult(resp.data);
      setDetailRefreshKey(k => k + 1);
    } catch (err: any) { setCsvError(err?.response?.data?.detail || 'CSV upload failed'); }
    finally { setCsvUploading(false); }
  };

  // ─── Fetch raw data ───
  const handleToggleRawData = async (batchCode: string) => {
    if (showRawData) { setShowRawData(false); setRawData(null); return; }
    try {
      const resp = await apiClient.get(`/batches/assays/${batchCode}`);
      setRawData(resp.data);
      setShowRawData(true);
    } catch { /* ignore */ }
  };

  // ─── Data row helpers ───
  const updateRow = (idx: number, field: keyof DataRow, value: string) => {
    setDataRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const addRow = () => setDataRows(prev => [...prev, emptyRow()]);
  const removeRow = (idx: number) => { if (dataRows.length > 1) setDataRows(prev => prev.filter((_, i) => i !== idx)); };

  const canCreate = formInitials.length >= 2 && formInitials.length <= 5 && /^[A-Z]{2,5}$/.test(formInitials);
  const canUpload = entryAssay.length >= 2 && dataRows.some(r => r.concentration && r.response_value);

  // =================================================================
  // RENDER: Creation form
  // =================================================================

  const renderCreateForm = () => (
    <div style={{ backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: 12, marginBottom: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 10 }}>New Batch</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={LABEL_STYLE}>Chemist Initials (2–5 letters) *</div>
          <input style={INPUT_STYLE} value={formInitials} onChange={e => setFormInitials(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5))} placeholder="e.g. DJDX" maxLength={5}
            onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>2–5 uppercase letters, auto-padded to 4 with X</div>
        </div>
        <div>
          <div style={LABEL_STYLE}>Salt / Solvate Form</div>
          <input style={INPUT_STYLE} value={formSalt} onChange={e => setFormSalt(e.target.value.slice(0, 20))} placeholder="e.g. HCl, Na, MeCN, hydrate" maxLength={20}
            onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Salt, solvate, co-crystal, or polymorph form</div>
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={LABEL_STYLE}>Structure (from selected compound)</div>
        <div style={{ padding: '6px 8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
          <div style={{ width: 120, height: 80, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StructureImage smiles={smiles} size={120} />
          </div>
          <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
            {smiles}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={LABEL_STYLE}>Purity (%)</div>
          <input type="number" style={INPUT_STYLE} value={formPurity} onChange={e => setFormPurity(e.target.value)} placeholder="e.g. 98.5" min={0} max={100} step={0.1}
            onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
        </div>
        <div>
          <div style={LABEL_STYLE}>Amount (mg)</div>
          <input type="number" style={INPUT_STYLE} value={formAmount} onChange={e => setFormAmount(e.target.value)} placeholder="e.g. 25.0" min={0} step={0.1}
            onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={LABEL_STYLE}>Synthesis Date</div>
        <input type="date" style={{ ...INPUT_STYLE, colorScheme: 'dark' }} value={formDate} onChange={e => setFormDate(e.target.value)}
          onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={LABEL_STYLE}>Notes</div>
        <textarea rows={2} style={{ ...INPUT_STYLE, resize: 'vertical' }} value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Optional notes..."
          onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
      </div>
      {createSuccess && <div style={{ fontSize: 12, color: '#10b981', marginBottom: 8 }}>{createSuccess}</div>}
      {createError && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8 }}>{createError}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleCreate} disabled={!canCreate || creating}
          style={{ ...TEAL_BTN, opacity: canCreate && !creating ? 1 : 0.5, cursor: canCreate && !creating ? 'pointer' : 'not-allowed' }}>
          {creating ? 'Creating...' : 'Create Batch'}
        </button>
        <button onClick={() => { setShowCreateForm(false); resetCreateForm(); }} style={CANCEL_BTN}>Cancel</button>
      </div>
    </div>
  );

  // =================================================================
  // RENDER: Submit for screen form
  // =================================================================

  const renderSubmitForm = () => (
    <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, padding: 10, backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>Submit for Screen</div>
      <div style={{ marginBottom: 6 }}>
        <div style={LABEL_STYLE}>Assay Name *</div>
        <input style={INPUT_STYLE} value={submitAssay} onChange={e => setSubmitAssay(e.target.value)} placeholder="e.g. EGFR IC50"
          onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={LABEL_STYLE}>Notes</div>
        <textarea rows={2} style={{ ...INPUT_STYLE, resize: 'vertical' }} value={submitNotes} onChange={e => setSubmitNotes(e.target.value)} placeholder="Instructions for biologist..."
          onFocus={e => { e.target.style.borderColor = '#0d9488'; }} onBlur={e => { e.target.style.borderColor = '#d1d5db'; }} />
      </div>
      {submitSuccess && <div style={{ fontSize: 12, color: '#10b981', marginBottom: 6 }}>{submitSuccess}</div>}
      {submitError && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 6 }}>{submitError}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSubmitForScreen} disabled={submitAssay.length < 2 || submitting}
          style={{ ...TEAL_BTN, backgroundColor: '#d97706', opacity: submitAssay.length >= 2 && !submitting ? 1 : 0.5, cursor: submitAssay.length >= 2 && !submitting ? 'pointer' : 'not-allowed' }}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
        <button onClick={() => { setShowSubmitForm(false); setSubmitAssay(''); setSubmitNotes(''); setSubmitError(null); }} style={CANCEL_BTN}>Cancel</button>
      </div>
    </div>
  );

  // =================================================================
  // RENDER: Manual data entry table
  // =================================================================

  const renderDataEntry = () => (
    <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, padding: 10, backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0d9488', marginBottom: 8 }}>Enter Assay Data</div>

      {/* Header: assay + measurement type */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div>
          <div style={LABEL_STYLE}>Assay Name *</div>
          {detail && detail.submissions.length > 0 ? (
            <select style={{ ...INPUT_STYLE, appearance: 'auto' }} value={entryAssay} onChange={e => setEntryAssay(e.target.value)}>
              <option value="">Select assay...</option>
              {detail.submissions.map(s => (
                <option key={s.submission_id} value={s.assay_name}>{s.assay_name}</option>
              ))}
            </select>
          ) : (
            <input style={INPUT_STYLE} value={entryAssay} onChange={e => setEntryAssay(e.target.value)} placeholder="e.g. EGFR IC50" />
          )}
        </div>
        <div>
          <div style={LABEL_STYLE}>Measurement Type</div>
          <select style={{ ...INPUT_STYLE, appearance: 'auto' }} value={entryMeasurement} onChange={e => setEntryMeasurement(e.target.value)}>
            {MEASUREMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {/* Data table */}
      <div style={{ overflowX: 'auto', marginBottom: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ color: '#6b7280', textTransform: 'uppercase' as const, fontSize: 10 }}>
              <th style={{ textAlign: 'left', padding: '4px 4px', fontWeight: 500 }}>Conc</th>
              <th style={{ textAlign: 'left', padding: '4px 4px', fontWeight: 500, width: 55 }}>Unit</th>
              <th style={{ textAlign: 'left', padding: '4px 4px', fontWeight: 500 }}>Response</th>
              <th style={{ textAlign: 'left', padding: '4px 4px', fontWeight: 500, width: 55 }}>Unit</th>
              <th style={{ textAlign: 'left', padding: '4px 4px', fontWeight: 500, width: 30 }}>Rep</th>
              <th style={{ width: 24 }}></th>
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, idx) => (
              <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                <td style={{ padding: 2 }}>
                  <input type="number" style={SMALL_INPUT} value={row.concentration} onChange={e => updateRow(idx, 'concentration', e.target.value)} placeholder="1000" />
                </td>
                <td style={{ padding: 2 }}>
                  <select style={{ ...SMALL_INPUT, fontFamily: 'inherit' }} value={row.concentration_unit} onChange={e => updateRow(idx, 'concentration_unit', e.target.value)}>
                    <option value="nM">nM</option><option value="uM">uM</option><option value="mM">mM</option>
                  </select>
                </td>
                <td style={{ padding: 2 }}>
                  <input type="number" style={SMALL_INPUT} value={row.response_value} onChange={e => updateRow(idx, 'response_value', e.target.value)} placeholder="50.0" />
                </td>
                <td style={{ padding: 2 }}>
                  <select style={{ ...SMALL_INPUT, fontFamily: 'inherit' }} value={row.response_unit} onChange={e => updateRow(idx, 'response_unit', e.target.value)}>
                    <option value="percent_activity">%act</option><option value="percent_inhibition">%inh</option>
                  </select>
                </td>
                <td style={{ padding: 2 }}>
                  <input type="number" style={{ ...SMALL_INPUT, width: 30 }} value={row.replicate_number} onChange={e => updateRow(idx, 'replicate_number', e.target.value)} min={1} />
                </td>
                <td style={{ padding: 2 }}>
                  {dataRows.length > 1 && (
                    <button onClick={() => removeRow(idx)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0 }}>
                      <X size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button onClick={addRow} style={{ fontSize: 11, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}>
        + Add Row
      </button>

      {uploadError && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 6 }}>{uploadError}</div>}

      {uploadResult && (
        <div style={{ padding: 8, backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 6, marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontFamily: 'monospace', fontWeight: 700, color: '#0d9488' }}>
            {uploadResult.statistics?.measurement_type || entryMeasurement} = {uploadResult.statistics?.mean_value?.toFixed(1) ?? '—'} nM
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            {uploadResult.statistics?.curve_fit_r2 != null && <span>R² = <b style={{ color: r2Color(uploadResult.statistics.curve_fit_r2) }}>{uploadResult.statistics.curve_fit_r2.toFixed(4)}</b></span>}
            {uploadResult.statistics?.hill_slope != null && <span>Hill = {uploadResult.statistics.hill_slope.toFixed(2)}</span>}
            {uploadResult.statistics?.n_replicates != null && <span>n = {uploadResult.statistics.n_replicates}</span>}
            <span>{uploadResult.results_inserted} data points uploaded</span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleUploadData} disabled={!canUpload || uploading}
          style={{ ...TEAL_BTN, opacity: canUpload && !uploading ? 1 : 0.5, cursor: canUpload && !uploading ? 'pointer' : 'not-allowed' }}>
          {uploading ? 'Uploading...' : 'Upload Data'}
        </button>
        <button onClick={() => { setShowDataEntry(false); setUploadError(null); setUploadResult(null); setDataRows([emptyRow(), emptyRow(), emptyRow()]); setEntryAssay(''); }} style={CANCEL_BTN}>Cancel</button>
      </div>
    </div>
  );

  // =================================================================
  // RENDER: CSV upload zone
  // =================================================================

  const renderCsvUpload = () => (
    <div onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
      <label style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        padding: '12px 8px', border: '1px dashed #99f6e4', borderRadius: 6,
        cursor: csvUploading ? 'wait' : 'pointer', textAlign: 'center',
        transition: 'border-color 0.15s',
      }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = '#0d9488'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = '#99f6e4'; }}
      >
        <Upload size={16} color="#0d9488" />
        <span style={{ fontSize: 11, color: '#0d9488' }}>{csvUploading ? 'Uploading...' : 'Upload CSV'}</span>
        <span style={{ fontSize: 10, color: '#6b7280', maxWidth: 300 }}>
          Columns: batch_code, assay_name, measurement_type, concentration, concentration_unit, response_value, response_unit, replicate_number
        </span>
        <input type="file" accept=".csv" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleCsvUpload(f); e.target.value = ''; }} />
      </label>
      {csvError && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{csvError}</div>}
      {csvResult && (
        <div style={{ fontSize: 12, color: '#10b981', marginTop: 4 }}>
          {Array.isArray(csvResult) ? `${csvResult.reduce((s: number, r: any) => s + (r.results_inserted || 0), 0)} data points uploaded across ${csvResult.length} batch(es)` : 'Upload complete'}
        </div>
      )}
    </div>
  );

  // =================================================================
  // RENDER: Enhanced statistics display
  // =================================================================

  const renderStatistics = (stats: BatchDetail['statistics'], batchCode: string) => (
    <div>
      {stats.map(stat => (
        <div key={stat.stat_id} style={{ padding: 8, backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 6, marginBottom: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 4 }}>{stat.assay_name}</div>
          {stat.mean_value != null && (
            <div style={{ fontSize: 16, fontFamily: 'monospace', fontWeight: 700, color: '#0d9488', marginBottom: 4 }}>
              {stat.measurement_type} = {stat.mean_value.toFixed(1)} nM
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 12px', fontSize: 11, color: '#6b7280' }}>
            {stat.curve_fit_r2 != null && <span>R² = <b style={{ color: r2Color(stat.curve_fit_r2) }}>{stat.curve_fit_r2.toFixed(4)}</b></span>}
            {stat.hill_slope != null && <span>Hill = {stat.hill_slope.toFixed(2)}</span>}
            {stat.n_replicates != null && <span>n = {stat.n_replicates}</span>}
            {stat.std_deviation != null && <span>SD = {stat.std_deviation.toFixed(2)}</span>}
          </div>
        </div>
      ))}

      {/* View raw data toggle */}
      <button onClick={e => { e.stopPropagation(); handleToggleRawData(batchCode); }}
        style={{ fontSize: 11, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        {showRawData ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        {showRawData ? 'Hide data points' : `View data points`}
      </button>

      {showRawData && rawData && (
        <div onClick={e => e.stopPropagation()} style={{ marginTop: 6, maxHeight: 200, overflowY: 'auto' }}>
          {(rawData.assays || []).map((assay: any, ai: number) => (
            <div key={ai}>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', marginBottom: 2, marginTop: ai > 0 ? 6 : 0 }}>{assay.assay_name}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontFamily: 'monospace' }}>
                <thead>
                  <tr style={{ color: '#6b7280' }}>
                    <th style={{ textAlign: 'left', padding: '2px 4px' }}>Conc</th>
                    <th style={{ textAlign: 'left', padding: '2px 4px' }}>Response</th>
                    <th style={{ textAlign: 'left', padding: '2px 4px' }}>Rep</th>
                  </tr>
                </thead>
                <tbody>
                  {(assay.data_points || []).map((dp: any, di: number) => (
                    <tr key={di} style={{ color: '#374151', backgroundColor: di % 2 === 0 ? 'transparent' : '#f8fafc' }}>
                      <td style={{ padding: '1px 4px' }}>{dp.concentration} {dp.concentration_unit}</td>
                      <td style={{ padding: '1px 4px' }}>{dp.response_value} {dp.response_unit === 'percent_activity' ? '%act' : dp.response_unit}</td>
                      <td style={{ padding: '1px 4px' }}>{dp.replicate_number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // =================================================================
  // RENDER: Expanded batch detail
  // =================================================================

  const renderExpandedDetail = (batch: BatchListItem) => {
    if (detailLoading) return <div style={{ fontSize: 12, color: '#9ca3af' }}>Loading details...</div>;
    if (!detail) return <div style={{ fontSize: 12, color: '#ef4444' }}>Failed to load details</div>;

    const hasSubmissions = detail.submissions.length > 0;
    const hasStats = detail.statistics.length > 0;

    return (
      <>
        {/* Registration */}
        <RegistrationSection
          batchCode={detail.batch_code}
          status={detail.status}
          inchiKey={batch.inchi_key}
          registration={detail.registration}
          onRegistered={() => setDetailRefreshKey(k => k + 1)}
        />

        {/* Submissions */}
        {hasSubmissions && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Submissions</div>
            {detail.submissions.map(sub => (
              <div key={sub.submission_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#374151', padding: '2px 0' }}>
                <span>{sub.assay_name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {sub.completed_at && <span style={{ fontSize: 10, color: '#6b7280' }}>{formatDate(sub.completed_at)}</span>}
                  <SubStatusBadge status={sub.status} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Statistics */}
        {hasStats && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Assay Results</div>
            {renderStatistics(detail.statistics, detail.batch_code)}
          </div>
        )}

        {/* Dose-response charts */}
        {hasStats && chartLoading && (
          <div style={{ height: 250, backgroundColor: '#f8fafc', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: 12, color: '#6b7280', animation: 'pulse 2s infinite' }}>Loading chart...</span>
          </div>
        )}
        {hasStats && chartData && (chartData.assays || []).map((assay: any, ai: number) => {
          const dataPoints = assay.data_points || [];
          if (dataPoints.length === 0) return null;
          const matchingStat = detail.statistics.find((s: any) => s.assay_name === assay.assay_name);
          if (!matchingStat) return null;
          return (
            <div key={ai} onClick={e => e.stopPropagation()} style={{ marginBottom: 8 }}>
              {(chartData.assays || []).length > 1 && (
                <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{assay.assay_name}</div>
              )}
              <DoseResponseChart
                dataPoints={dataPoints}
                statistics={matchingStat}
                batchCode={detail.batch_code}
                assayName={assay.assay_name}
              />
            </div>
          );
        })}

        {/* Submit for screen — only for active batches without submissions */}
        {detail.status === 'active' && !hasSubmissions && !showSubmitForm && (
          <button onClick={e => { e.stopPropagation(); setShowSubmitForm(true); }}
            style={{ ...TEAL_BTN, backgroundColor: '#d97706', fontSize: 11, padding: '4px 12px', marginTop: 4 }}>
            Submit for Screen
          </button>
        )}
        {showSubmitForm && renderSubmitForm()}

        {/* Assay data upload — for batches with submissions */}
        {hasSubmissions && !showDataEntry && !uploadResult && (
          <div onClick={e => e.stopPropagation()} style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => { setShowDataEntry(true); setUploadResult(null); if (detail.submissions.length > 0) setEntryAssay(detail.submissions[0].assay_name); }}
              style={{ fontSize: 11, color: '#0d9488', background: 'none', border: '1px solid #99f6e4', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>
              Enter Data
            </button>
          </div>
        )}
        {showDataEntry && renderDataEntry()}

        {/* CSV upload zone — always available when expanded */}
        {hasSubmissions && !showDataEntry && renderCsvUpload()}

        {/* No data message */}
        {!hasStats && !hasSubmissions && !showSubmitForm && detail.status !== 'active' && (
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>No assay data yet</div>
        )}

        {/* Notes */}
        {detail.notes && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Note: {detail.notes}</div>
        )}

        {/* ELN Notebook Entries */}
        <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
          <ELNSection
            batchCode={detail.batch_code}
            smiles={detail.smiles || smiles}
            projectId={projectId}
            latestAssay={detail.submissions.length > 0 ? detail.submissions[0].assay_name : undefined}
          />
        </div>
      </>
    );
  };

  // =================================================================
  // MAIN RENDER
  // =================================================================

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>Loading batches...</div>;
  if (error) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ef4444' }}>{error}</div>;
  if (!projectId) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8 }}>
      <FlaskConical size={32} color="#6b7280" />
      <div style={{ fontSize: 14, color: '#9ca3af' }}>No project selected</div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>Open a file within a project to see batches</div>
    </div>
  );

  if (batches.length === 0 && !showCreateForm) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 8 }}>
      <FlaskConical size={32} color="#6b7280" />
      <div style={{ fontSize: 14, color: '#9ca3af' }}>No batches yet</div>
      <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', maxWidth: 260 }}>Create a batch to track synthesis and assay data for this compound</div>
      <button onClick={() => setShowCreateForm(true)} style={{ ...TEAL_BTN, marginTop: 8 }}>Create Batch</button>
    </div>
  );

  if (batches.length === 0 && showCreateForm) return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{renderCreateForm()}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
        {showCreateForm ? renderCreateForm() : (
          <button onClick={() => setShowCreateForm(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 11, borderRadius: 4, border: '1px solid #99f6e4', backgroundColor: 'transparent', color: '#0d9488', cursor: 'pointer' }}>
            <Plus size={12} /> New Batch
          </button>
        )}
        {!showCreateForm && (
          <a href="/workbench-api/eln/" target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#9ca3af', textDecoration: 'none' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={10} height={10}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Lab Notebook Templates
          </a>
        )}
      </div>

      {batches.map(batch => {
        const isExpanded = expandedCode === batch.batch_code;
        return (
          <div key={batch.batch_id} onClick={() => handleCardClick(batch.batch_code)}
            style={{
              backgroundColor: isExpanded ? '#f0fdfa' : '#ffffff',
              border: isExpanded ? '1px solid #99f6e4' : '1px solid #e2e8f0',
              borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
              transition: 'background-color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc'; }}
            onMouseLeave={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.backgroundColor = '#ffffff'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {batch.smiles && (
                  <div style={{ flexShrink: 0, width: 60, height: 50, borderRadius: 4, overflow: 'hidden', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <StructureImage smiles={batch.smiles} size={50} />
                  </div>
                )}
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#111827' }}>{batch.batch_code}</span>
              </div>
              <BatchStatusBadge status={batch.status} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12, color: '#6b7280' }}>
              <span>Chemist: {batch.chemist_initials}</span>
              {batch.purity_percent != null && <span>Purity: {batch.purity_percent}%</span>}
              {batch.amount_mg != null && <span>Amount: {batch.amount_mg} mg</span>}
              <span>Created: {formatDate(batch.created_at)}</span>
            </div>
            {isExpanded && (
              <div style={{ marginTop: 10, borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
                {renderExpandedDetail(batch)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BatchesTab;
